import moment from "moment";
import {
  FinancialRow,
  ProductMixRow,
  CommissaryOrder,
  PayoutAdjustment,
  VendorConfig,
  VendorLocationData,
  EventDay,
  ItemSales,
  ItemDayData,
  DeductionCategory,
  EventStats,
} from "./types";

const usd = (cents: number) => {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

export { usd };

/** Extract event days — use event start/end dates if available, otherwise from data */
export const getEventDays = (
  financialData: FinancialRow[],
  startDate?: string | null,
  endDate?: string | null
): EventDay[] => {
  // If we have event dates, generate the range
  if (startDate && endDate) {
    const days: EventDay[] = [];
    const current = moment(startDate);
    const end = moment(endDate);
    while (current.isSameOrBefore(end, "day")) {
      const d = current.format("YYYY-MM-DD");
      days.push({
        date: d,
        label: current.format("dddd"),
        shortLabel: current.format("ddd M/D"),
      });
      current.add(1, "day");
    }
    return days;
  }

  // Fallback: extract from data
  const dateSet = new Set<string>();
  financialData.forEach((row) => {
    if (row.transaction_date) {
      dateSet.add(row.transaction_date.split("T")[0]);
    }
  });

  return Array.from(dateSet)
    .sort()
    .map((d) => ({
      date: d,
      label: moment(d).format("dddd"),
      shortLabel: moment(d).format("ddd M/D"),
    }));
};

/** Build per-item, per-day sales breakdown for a vendor-location */
const buildItemSales = (
  productMixData: ProductMixRow[],
  vendorId: number,
  locationId: number,
  eventDays: EventDay[]
): ItemSales[] => {
  const itemMap: Record<string, ItemSales> = {};

  productMixData
    .filter((row) => row.vendor_id === vendorId && row.location_id === locationId)
    .forEach((row) => {
      const itemName = row.pos_name || row.description || "Unknown Item";
      const date = (row.transaction_date || "").split("T")[0];

      if (!itemMap[itemName]) {
        const byDay: Record<string, ItemDayData> = {};
        eventDays.forEach((d) => {
          byDay[d.date] = { credit: 0, rfid: 0, cashBalance: 0, units: 0 };
        });
        itemMap[itemName] = {
          itemName,
          byDay,
          totalCredit: 0,
          totalRfid: 0,
          totalCashBalance: 0,
          totalUnits: 0,
        };
      }

      const item = itemMap[itemName];
      const credit = row.gross_credit_sales || 0;
      const rfid = row.gross_rfid_sales || 0;
      const cashBalance = row.gross_cash_balance_sales || 0;
      const units = row.total_units_sold || 0;

      if (item.byDay[date]) {
        item.byDay[date].credit += credit;
        item.byDay[date].rfid += rfid;
        item.byDay[date].cashBalance += cashBalance;
        item.byDay[date].units += units;
      }

      item.totalCredit += credit;
      item.totalRfid += rfid;
      item.totalCashBalance += cashBalance;
      item.totalUnits += units;
    });

  return Object.values(itemMap);
};

/** Group commissary orders into deduction categories by item name */
const buildDeductionCategories = (
  orders: CommissaryOrder[]
): DeductionCategory[] => {
  const categoryMap: Record<string, DeductionCategory> = {};

  orders.forEach((order) => {
    const items = order.items;
    if (items && Array.isArray(items) && items.length > 0) {
      items.forEach((item) => {
        const name = item.item_name || "Other";
        if (!categoryMap[name]) {
          categoryMap[name] = {
            categoryName: name,
            orders: [],
            totalAmount: 0,
            totalCount: 0,
          };
        }
        const cat = categoryMap[name];
        if (!cat.orders.find((o) => o.id === order.id)) {
          cat.orders.push(order);
        }
        cat.totalAmount += item.total_price || 0;
        cat.totalCount += item.quantity || 1;
      });
    } else {
      const name = "Commissary Order";
      if (!categoryMap[name]) {
        categoryMap[name] = {
          categoryName: name,
          orders: [],
          totalAmount: 0,
          totalCount: 0,
        };
      }
      categoryMap[name].orders.push(order);
      categoryMap[name].totalAmount += order.total || 0;
      categoryMap[name].totalCount += 1;
    }
  });

  return Object.values(categoryMap);
};

/** Process all data into VendorLocationData array */
export const processData = (
  financialData: FinancialRow[],
  productMixData: ProductMixRow[],
  commissaryOrders: CommissaryOrder[],
  payoutAdjustments: PayoutAdjustment[],
  vendorConfigs: VendorConfig[],
  eventStartDate?: string | null,
  eventEndDate?: string | null
): VendorLocationData[] => {
  const eventDays = getEventDays(financialData, eventStartDate, eventEndDate);
  const vendorConfigMap = new Map(vendorConfigs.map((v) => [v.id, v]));

  // Group financial data by vendor+location
  const groupKey = (row: FinancialRow) => `${row.vendor_id}::${row.location_id}`;
  const groups: Record<string, FinancialRow[]> = {};

  financialData.forEach((row) => {
    const key = groupKey(row);
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  });

  // Group commissary orders by vendor (fulfilled only — used for item-name categories)
  const ordersByVendor: Record<number, CommissaryOrder[]> = {};
  commissaryOrders.forEach((order) => {
    if (!ordersByVendor[order.vendor_id]) ordersByVendor[order.vendor_id] = [];
    ordersByVendor[order.vendor_id].push(order);
  });

  // Group payout adjustments by vendor
  const adjustmentsByVendor: Record<number, PayoutAdjustment[]> = {};
  payoutAdjustments.forEach((entry) => {
    if (!adjustmentsByVendor[entry.vendor_id]) adjustmentsByVendor[entry.vendor_id] = [];
    adjustmentsByVendor[entry.vendor_id].push(entry);
  });

  return Object.entries(groups).map(([_, rows]) => {
    const first = rows[0];
    const vendorId = first.vendor_id;
    const config = vendorConfigMap.get(vendorId);
    const revenueShare = (config?.revenue_share ?? 0) * 100;
    const flatFee = config?.flat_fee ?? 0;
    const salesTaxResponsible = config?.sales_tax_responsible ?? false;

    // Aggregate financials across all days for this vendor-location
    let grossSales = 0;
    let grossCredit = 0;
    let grossRfid = 0;
    let grossCashBalance = 0;
    let salesTax = 0;
    let tips = 0;
    let totalDiscounts = 0;
    let totalRefunds = 0;

    const dayTotals: VendorLocationData["dayTotals"] = {};
    eventDays.forEach((d) => {
      dayTotals[d.date] = { credit: 0, rfid: 0, cashBalance: 0, tax: 0, tips: 0 };
    });

    rows.forEach((row) => {
      const date = (row.transaction_date || "").split("T")[0];
      const credit = row.gross_credit_sales || 0;
      const rfid = row.gross_rfid_sales || 0;
      const cashBal = row.gross_cash_balance_sales || 0;
      const cash = row.gross_cash_sales || 0;
      const token = row.gross_token_sales || 0;
      const promo = row.gross_promo_balance_sales || 0;
      const rowGross = credit + rfid + cashBal + cash + token + promo;

      grossSales += rowGross;
      grossCredit += credit;
      grossRfid += rfid;
      grossCashBalance += cashBal;
      salesTax += row.total_tax || 0;
      tips += row.total_tips || 0;
      totalDiscounts += row.total_discounts || 0;
      totalRefunds += row.total_refunds || 0;

      if (dayTotals[date]) {
        dayTotals[date].credit += credit;
        dayTotals[date].rfid += rfid;
        dayTotals[date].cashBalance += cashBal;
        dayTotals[date].tax += row.total_tax || 0;
        dayTotals[date].tips += row.total_tips || 0;
      }
    });

    // Payout calculations
    const netSales = grossSales - salesTax;
    const commissionAmount = Math.round(netSales * (revenueShare / 100));
    const vendorCommission = netSales - commissionAmount;

    const fulfilledOrders = ordersByVendor[vendorId] || [];
    const vendorEntries = adjustmentsByVendor[vendorId] || [];
    const manualDeductions = vendorEntries.filter((e) => e.kind === "deduction");
    const adjustments = vendorEntries.filter((e) => e.kind === "credit");

    const deductionCategories = buildDeductionCategories(fulfilledOrders);
    const commissaryTotal = fulfilledOrders.reduce((sum, o) => sum + (o.total || 0), 0);
    const totalManualDeductions = manualDeductions.reduce((sum, e) => sum + e.amount, 0);
    const totalDeductions = commissaryTotal + totalManualDeductions;
    const totalAdjustments = adjustments.reduce((sum, e) => sum + e.amount, 0);

    // vendors.flat_fee is stored in cents: VendorCreate/VendorEdit multiply the
    // dollar input by 100 on save (since 2024-05), the vendor list edits it with
    // displayFactor 100, and the vertical sales report reads it as cents. Do not
    // convert again here; doing so showed a $500 fee as $50,000.
    const participationFee = Math.round(flatFee);

    // If vendor is sales-tax-responsible, pass the collected tax through to them
    // so they can remit it. Otherwise the organizer keeps it (current default).
    const taxPassThrough = salesTaxResponsible ? salesTax : 0;

    const totalDue =
      vendorCommission + tips + taxPassThrough + totalAdjustments - totalDeductions - participationFee;

    // Item sales by day
    const items = buildItemSales(productMixData, vendorId, first.location_id, eventDays);

    return {
      vendorId,
      vendorName: first.vendor_name,
      locationId: first.location_id,
      locationName: first.location_name,
      eventName: first.event_name,
      revenueShare,
      flatFee: participationFee,
      salesTaxResponsible,
      eventDays,
      items,
      grossSales,
      grossCredit,
      grossRfid,
      grossCashBalance,
      salesTax,
      tips,
      totalDiscounts,
      totalRefunds,
      dayTotals,
      netSales,
      commissionAmount,
      vendorCommission,
      deductionCategories,
      commissaryTotal,
      manualDeductions,
      totalManualDeductions,
      totalDeductions,
      adjustments,
      totalAdjustments,
      participationFee,
      totalDue,
    };
  });
};

/** Compute event-level aggregated stats */
export const computeEventStats = (vendors: VendorLocationData[]): EventStats => {
  return vendors.reduce(
    (acc, v) => ({
      totalRevenue: acc.totalRevenue + v.grossSales,
      totalTax: acc.totalTax + v.salesTax,
      totalPayoutsDue: acc.totalPayoutsDue + v.totalDue,
      totalDeductions: acc.totalDeductions + v.totalDeductions,
      totalAdjustments: acc.totalAdjustments + v.totalAdjustments,
      vendorLocationCount: acc.vendorLocationCount + 1,
    }),
    {
      totalRevenue: 0,
      totalTax: 0,
      totalPayoutsDue: 0,
      totalDeductions: 0,
      totalAdjustments: 0,
      vendorLocationCount: 0,
    }
  );
};
